import React, { useEffect, useState } from 'react';
import { ChevronDownIcon } from '@radix-ui/react-icons';

import { AvatarCreatableSelect } from '../../../components/select/components/avatar-creatable-select/avatar-creatable-select';
import { RoleDropdown, type RoleTypes } from './role-dropdown';

import { type AvatarOption } from '../../../components/select/components/avatar-select/avatar-select-option';
import { type SelectRefType } from '../../../components/select';
import { type Space } from '../../../app/entities/space';

import styles from './member-role-select.module.scss';
type ContactSelectProps = Omit<
  React.ComponentProps<typeof AvatarCreatableSelect>,
  'value' | 'onChange'
> & {
  space: Space;
  options: React.ComponentProps<typeof AvatarCreatableSelect>['options'];
  value?: string;
  isLoading?: boolean;
  errorMessage?: string;
  setErrorMessage?: (error?: string) => void;
  onChange?: (value?: string) => void;
  role?: RoleTypes;
  onRoleChange?: (role?: RoleTypes) => void;
};

export const MemberRoleSelector = React.forwardRef<SelectRefType<AvatarOption>, ContactSelectProps>(
  (
    {
      space,
      options,
      value,
      isLoading,
      errorMessage,
      role,
      onChange,
      setErrorMessage,
      onRoleChange,
    },
    ref,
  ) => {
    const [hasOptions, setHasOptions] = useState<boolean>();
    const [showDropdown, setShowDropdown] = useState<boolean>();

    const dropdownWidth = React.useRef<number>(0);

    useEffect(() => {
      if (!hasOptions && value) {
        setShowDropdown(true);
      } else {
        setShowDropdown(false);
      }
    }, [hasOptions, value]);

    const handleChange: React.ComponentProps<typeof AvatarCreatableSelect>['onChange'] = (
      option: any,
    ) => {
      onChange?.(option?.value);
      onRoleChange?.('guest');
    };

    const handleCreate: React.ComponentProps<typeof AvatarCreatableSelect>['onCreate'] = (
      value: string,
    ) => {
      onChange?.(value);
    };

    const controlContainerCallbackRef = (node: HTMLDivElement) => {
      if (!node) {
        return;
      }
      dropdownWidth.current = node.clientWidth;
    };

    return (
      <div className={styles['container']} data-error={!!errorMessage} ref={controlContainerCallbackRef}>
        <AvatarCreatableSelect
          ref={ref}
          name="invite-member"
          placeholder={'name@domain.com'}
          options={options}
          onChange={handleChange}
          onCreate={handleCreate}
          isLoading={isLoading}
          width={dropdownWidth.current}
          menuPortalTarget={document.body}
          setHasOptions={setHasOptions}
          onInputChange={(value) => {
            onChange?.(value);
            setErrorMessage?.(undefined);
          }}
          isSearchable
          isClearable
          backspaceRemovesValue
        />

        <div className={styles['dropdown-wrapper']} data-hide={!showDropdown}>
          <RoleDropdown
            trigger={(label) => (
              <div className={styles['trigger']}>
                <p className={styles['label']}>{label}</p>

                <ChevronDownIcon color="var(--color-grayscale-shuttle)" />
              </div>
            )}
            width={dropdownWidth.current - 8}
            space={space}
            onChange={onRoleChange}
            role={role}
          />
        </div>

        {errorMessage && <span className={styles['error']}>{errorMessage}</span>}
      </div>
    );
  },
);

MemberRoleSelector.displayName = 'MemberRoleSelector';

